import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { userKeys } from '@/features/user/api/queries';
import { useAnalytics } from '@/lib/analytics';
import { useNotifications } from '@/stores/notifications';

import { updateAddress, type UpdateAddressPayload } from './update-address';
import {
  updateProfile,
  updateProfileName,
  type UpdateProfilePayload,
  type UpdateProfileNamePayload,
} from './update-profile';

type ApiErrorResponse = {
  status?: string;
  message?: string;
  exception?: string;
};

// Helper to determine update type from payload (check in order: avatar, mobile, email)
const getUpdateType = (
  payload: UpdateProfilePayload
): 'email' | 'phone' | 'avatar' => {
  if ('avatar' in payload) return 'avatar';
  if ('mobile_number' in payload) return 'phone';
  return 'email';
};

// Query Keys for Profile Updates
const profileUpdateKeys = {
  all: ['profileUpdate'] as const,
  update: () => [...profileUpdateKeys.all, 'update'] as const,
  updateName: () => [...profileUpdateKeys.all, 'updateName'] as const,
  updateAddress: () => [...profileUpdateKeys.all, 'updateAddress'] as const,
} as const;

// Update Profile Mutation
const useUpdateProfile = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: (data: UpdateProfilePayload) => updateProfile(data),
    mutationKey: profileUpdateKeys.update(),
    onSuccess: (_, variables) => {
      // Determine update type
      const updateType = getUpdateType(variables);

      // Track analytics event (without personal information)
      trackEvent('updatedProfile', 1, {
        profileId: variables.id,
        updateType,
      });

      // Invalidate user queries to refresh profile data
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      addNotification({
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = (error as AxiosError<ApiErrorResponse>)?.response
        ?.data?.message;

      const displayMessage =
        errorMessage && errorMessage.length < 40
          ? errorMessage
          : 'Failed to update profile. Please try again';

      addNotification({
        title: 'Update Failed',
        message: displayMessage,
        type: 'error',
      });
      onError?.();
    },
  });
};

// Update Profile Name Mutation (no OTP — used by the verify-name gate)
const useUpdateProfileName = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: (data: UpdateProfileNamePayload) => updateProfileName(data),
    mutationKey: profileUpdateKeys.updateName(),
    onSuccess: (_, variables) => {
      trackEvent('updatedProfile', 1, {
        profileId: variables.id,
        updateType: 'name',
      });

      // Refresh profile data so the cleared `unverified_name` flag dismisses
      // the verify-name gate.
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      addNotification({
        title: 'Name Updated',
        message: 'Your name has been saved successfully',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = (error as AxiosError<ApiErrorResponse>)?.response
        ?.data?.message;

      const displayMessage =
        errorMessage && errorMessage.length < 40
          ? errorMessage
          : 'Failed to update name. Please try again';

      addNotification({
        title: 'Update Failed',
        message: displayMessage,
        type: 'error',
      });
      onError?.();
    },
  });
};

// Update Address Mutation
const useUpdateAddress = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: (data: UpdateAddressPayload) => updateAddress(data),
    mutationKey: profileUpdateKeys.updateAddress(),
    onSuccess: (_, variables) => {
      // Track analytics event for address update (without personal information)
      trackEvent('updatedProfile', 1, {
        profileId: variables.lead_id,
        updateType: 'address',
      });

      // Invalidate user queries to refresh profile data
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      addNotification({
        title: 'Address Updated',
        message: 'Your address has been updated successfully',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = (error as AxiosError<ApiErrorResponse>)?.response
        ?.data?.message;

      const displayMessage =
        errorMessage && errorMessage.length < 40
          ? errorMessage
          : 'Failed to update address. Please try again';

      addNotification({
        title: 'Update Failed',
        message: displayMessage,
        type: 'error',
      });
      onError?.();
    },
  });
};

// Profile Update Hook
export const useProfileUpdate = () => {
  const updateProfileMutation = useUpdateProfile();

  return {
    // Profile operations
    updateProfile: updateProfileMutation.mutate,

    // Loading states
    isUpdatingProfile: updateProfileMutation.isPending,

    // Error states
    updateProfileError: updateProfileMutation.error,

    // Reset functions
    resetUpdateProfileError: updateProfileMutation.reset,
  };
};

// Profile Name Update Hook (no OTP)
export const useProfileNameUpdate = () => {
  const updateProfileNameMutation = useUpdateProfileName();

  return {
    // Name operation
    updateName: updateProfileNameMutation.mutate,

    // Loading state
    isUpdatingName: updateProfileNameMutation.isPending,

    // Error state
    updateNameError: updateProfileNameMutation.error,

    // Reset function
    resetUpdateNameError: updateProfileNameMutation.reset,
  };
};

// Address Update Hook
export const useAddressUpdate = () => {
  const updateAddressMutation = useUpdateAddress();

  return {
    // Address operations
    updateAddress: updateAddressMutation.mutate,

    // Loading states
    isUpdatingAddress: updateAddressMutation.isPending,

    // Error states
    updateAddressError: updateAddressMutation.error,

    // Reset functions
    resetUpdateAddressError: updateAddressMutation.reset,
  };
};
